<script setup lang="ts">
import { ref, computed, watch, onMounted, toRef } from 'vue'

import { UploadFilled } from '@element-plus/icons-vue'
import { Icon } from '@iconify/vue'
import { storeToRefs } from 'pinia'

import CropperDialog from '@/components/frontend/shorter-list/dialog/CropperDialog.vue'
import SingleShorterUrlForm from '@/components/frontend/shorter-list/form/SingleShorterUrlForm.vue'
import ImageUploader from '@/components/frontend/shorter-list/shared/ImageUploader.vue'
import OrgSortTag from '@/components/frontend/shorter-list/shared/OrgSortTag.vue'
import BaseButton from '@/components/UI/BaseButton.vue'

import { useFEUrlDialogMode } from '@/composables/useFEUrlDialogMode'

import { autoFocusFirstInput } from '@/utils/common/formHandler'
import { getRadioColumns } from '@/utils/ui/getRadioColumns'
import { notifyError, notifySuccess } from '@/utils/ui/handleNotification'

import * as aggr from '@/api-generator/aggr'
import { feUrlGroupingAPI } from '@/APIs'
import { ORG_SORT_TAG_VARIANTS } from '@/constant/client/orgSortTag'
import { ShorterDialogMode } from '@/constant/client/shorterDialog'
import appStore from '@/stores'

import type {
  GroupUrlSubmitData,
  ShorterGroupFormModel,
  SingleUrlSubmitData,
  ShorterSingleFormModel
} from '@/types/shorterUrlForm'
import type { FormInstance, FormRules, UploadFile, UploadInstance } from 'element-plus'

const shortUrlStore = appStore.useShortUrlStore
const orgSortTagStore = appStore.useOrgSortTagStore
const { subTags, selectedCompanyUuId, selectedOrganizationId } = storeToRefs(shortUrlStore)

/**
 * 群組表單 Props
 */
interface GroupUrlFormProps {
  mode: ShorterDialogMode
  onSubmit: (data: GroupUrlSubmitData) => void | Promise<void>
  onCancel: () => void
}

const props = defineProps<GroupUrlFormProps>()

const formData = defineModel<ShorterGroupFormModel>({ default: () => ({}) })

const { buttonTexts } = useFEUrlDialogMode(toRef(props, 'mode'))

// ==================== Form State ====================
const formRef = ref<FormInstance | null>(null)
const csvUploadRef = ref<UploadInstance | null>(null)
const firstInput = ref<HTMLInputElement | null>(null)
const isLoading = ref(false)

// CSV 相關
const csvBase64Url = ref('')

// OG Meta 相關
const showOgInfo = ref(false)
const selectedImage = ref('')
const cropperDialogVisible = ref(false)
const croppedImage = ref<string | null>(null)

// 2025/04/28 暫時隱藏 Tape ID 開關
const showTraceTapeIdSwitch = ref(false)

// 群組編輯模式下的個別建立開關
const useSingleUrlUpload = ref(false)

// 個別短網址表單資料
const singleFormData = ref<ShorterSingleFormModel>({
  urlType: aggr.UrlType.Url,
  projectName: '',
  subtagId: undefined,
  originalUrl: '',
  chineseTag: '',
  title: '',
  description: '',
  imageUrl: '',
  uploadImageBase64: '',
  directUrl: '',
  enableTraceTapeId: true
})

// ==================== Computed ====================
const isGroupEditMode = computed(() => props.mode === ShorterDialogMode.GROUP_EDIT)

const radioColumns = computed(() => getRadioColumns(subTags.value))

const submitButtonText = computed(() => {
  const texts = buttonTexts.value
  return isLoading.value ? texts.loadingBtnText : texts.defaultBtnText
})

// ==================== Form Rules ====================
interface ValidateCallback {
  (error?: Error): void
}

const validateCsvData = (_rule: unknown, value: string, callback: ValidateCallback) => {
  if (!isGroupEditMode.value && !value) {
    callback(new Error('請上傳CSV檔案'))
  } else {
    callback()
  }
}

const formRules: FormRules<ShorterGroupFormModel> = {
  projectName: [{ required: true, message: '請輸入專案名稱', trigger: 'blur' }],
  subtagId: [{ required: true, message: '請選擇一個受眾標籤', trigger: 'change' }],
  csvData: [{ validator: validateCsvData, trigger: 'change' }]
}

/**
 * 下載群組範本 CSV
 */
const downloadGroupExampleCSV = () => {
  const fileUrl = import.meta.env.VITE_GROUP_EXAMPLE_CSV_URL
  const fileName = 'GroupCreateExample.csv'

  const link = document.createElement('a')
  link.href = fileUrl
  link.download = fileName
  link.click()
  link.remove()
}

/**
 * 上傳 CSV 檔案
 */
const handleFileChange = async (file: UploadFile) => {
  if (!file.raw) {
    return
  }

  // 驗證檔案類型，只允許 .csv 檔案
  const fileName = file.name.toLowerCase()
  if (!fileName.endsWith('.csv')) {
    ElMessage.error('請上傳 CSV 格式的檔案')
    csvUploadRef.value?.clearFiles()
    handleFileRemove()
    return
  }

  const reader = new FileReader()
  reader.onload = async () => {
    const base64Data = reader.result ? reader.result.toString().split(',')[1] : ''
    if (base64Data) {
      csvBase64Url.value = base64Data
      formData.value.csvData = base64Data
      formRef.value?.clearValidate(['csvData'])
    } else {
      notifyError('檔案轉換失敗')
    }
  }
  reader.onerror = () => {
    notifyError('檔案讀取錯誤')
  }

  reader.readAsDataURL(file.raw)
}

const handleFileRemove = () => {
  csvBase64Url.value = ''
  formData.value.csvData = ''
}

/**
 * 圖片上傳處理
 */
const handleUploadImage = (file: UploadFile): void => {
  if (!file.raw) {
    return
  }

  const reader = new FileReader()
  reader.onload = (e) => {
    if (e.target && e.target.result) {
      selectedImage.value = e.target.result as string
      cropperDialogVisible.value = true
    }
  }
  reader.onerror = () => {
    ElMessage.error('圖片讀取失敗')
  }
  reader.readAsDataURL(file.raw)
}

/**
 * 圖片上傳錯誤處理
 */
const handleUploadError = (message: string) => {
  console.error('圖片上傳錯誤:', message)
}

const handleUpdateCroppedImage = (imgDataUrl: string) => {
  croppedImage.value = imgDataUrl
}

const handleUpdateCroppedImageBase64 = (base64Data: string) => {
  formData.value.uploadImageBase64 = base64Data
}

const toggleOgInfo = () => {
  showOgInfo.value = !showOgInfo.value
}

/**
 * 準備提交資料
 */
const prepareSubmitData = (): GroupUrlSubmitData => {
  const baseData: GroupUrlSubmitData = {
    projectName: formData.value.projectName || '',
    subtagId: Number(formData.value.subtagId),
    title: formData.value.title || null,
    description: formData.value.description || null,
    enableTraceTapeId: formData.value.enableTraceTapeId ?? true,
    orgTagId: formData.value.orgTagId ?? undefined
  }

  // 建立模式
  if (!isGroupEditMode.value) {
    return {
      ...baseData,
      csvData: csvBase64Url.value || undefined,
      useCsvFileUpload: true,
      uploadImageBase64: formData.value.uploadImageBase64 || undefined
    }
  }

  // 編輯模式
  return {
    ...baseData,
    urlGroupId: formData.value.urlGroupId || undefined,
    useCsvFileUpload: !!csvBase64Url.value,
    csvData: csvBase64Url.value || undefined,
    hasUpdateImage: !!formData.value.uploadImageBase64,
    newUploadImageBase64: formData.value.uploadImageBase64 || undefined
  }
}

/**
 * 提交表單
 */
const submitForm = () => {
  if (!formRef.value) {
    return
  }

  formRef.value.validate(async (valid) => {
    if (!valid) {
      console.error('表單驗證失敗')
      return
    }

    isLoading.value = true
    try {
      const submitData = prepareSubmitData()
      await props.onSubmit(submitData)
    } catch (error) {
      console.error('提交失敗:', error)
    } finally {
      isLoading.value = false
    }
  })
}

/**
 * 取消
 */
const handleCancel = () => {
  props.onCancel()
}

/**
 * 處理在群組內建立個別短網址
 */
const handleSingleUrlSubmit = async (submitData: SingleUrlSubmitData) => {
  const reqBody: aggr.FEUrlGroupingSingleUrlCreateRq = {
    companyUuId: selectedCompanyUuId.value,
    companyOrganizationUuid: selectedOrganizationId.value,
    urlGroupId: formData.value.urlGroupId ?? '',
    ...submitData
  }

  await feUrlGroupingAPI.createUrlGroupingSingleUrl(reqBody)
  notifySuccess('短網址已成功建立')
  props.onCancel()
}

/**
 * 切換分類標記選擇
 * 點選相同的標記時取消選擇
 */
const toggleOrgTag = (tagId: number | undefined) => {
  if (!tagId) {
    return
  }

  if (formData.value.orgTagId === tagId) {
    formData.value.orgTagId = null
  } else {
    formData.value.orgTagId = tagId
  }
}

// ==================== Watchers ====================

// 監聽個別建立開關
watch(useSingleUrlUpload, (newVal) => {
  if (newVal) {
    formRef.value?.clearValidate(['csvData'])

    // 初始化個別短網址表單，帶入群組的 subtagId
    singleFormData.value = {
      urlType: aggr.UrlType.Url,
      projectName: '',
      subtagId: formData.value.subtagId,
      originalUrl: '',
      chineseTag: '',
      title: '',
      description: '',
      imageUrl: '',
      uploadImageBase64: '',
      directUrl: '',
      enableTraceTapeId: true
    }
  }
})

watch(
  () => formData.value.thumbnailImageUrl,
  (newVal) => {
    if (newVal) {
      handleUpdateCroppedImage(newVal)
    }
  }
)

// ==================== Lifecycle ====================
onMounted(async () => {
  await autoFocusFirstInput(firstInput.value)
})
</script>

<template>
  <el-form ref="formRef" class="shorter-url-form" :model="formData" :validate-on-rule-change="false" :rules="formRules">
    <!-- 專案名稱 -->
    <el-form-item class="form-title" prop="projectName">
      <span class="shorter-url-form-label mb-2">專案名稱</span>
      <el-input ref="firstInput" v-model="formData.projectName" autocomplete="off" />
    </el-form-item>

    <!-- 受眾標籤 -->
    <el-form-item class="form-title subtag-list" prop="subtagId">
      <span class="shorter-url-form-label mb-2">受眾標籤</span>
      <el-radio-group
        v-model="formData.subtagId"
        class="radio-group"
        :style="{
          display: 'grid',
          gridTemplateColumns: `repeat(${radioColumns}, 1fr)`
        }"
      >
        <el-radio
          v-for="subTag in subTags"
          :key="subTag.subtagId"
          :value="subTag.subtagId"
          :label="subTag.subtagId"
          :disabled="isGroupEditMode"
        >
          <span class="text-sm font-normal"> {{ subTag.subtagName }}</span>
        </el-radio>
      </el-radio-group>
    </el-form-item>

    <!-- 上傳檔案 -->
    <el-form-item prop="csvData" style="margin-bottom: 24px">
      <div class="mb-3 flex w-full items-center justify-between">
        <div class="upload-switch">
          <span class="shorter-url-form-label">上傳檔案</span>
          <template v-if="isGroupEditMode">
            <el-switch v-model="useSingleUrlUpload" class="switch" size="small" style="--el-switch-on-color: #1c7af3" />
            <div class="hintText">
              {{ useSingleUrlUpload ? '個別建立' : 'CSV 上傳' }}
            </div>
          </template>
        </div>
        <button
          type="button"
          class="rounded-[4px] bg-[#e6f4ff] px-2 py-1.5 text-xs font-medium text-app-brand-blue-500 hover:bg-[#cce6ff]"
          @click="downloadGroupExampleCSV"
        >
          下載範本
        </button>
      </div>

      <!-- CSV 上傳 -->
      <el-upload
        v-if="!useSingleUrlUpload"
        ref="csvUploadRef"
        class="w-full"
        :drag="true"
        :auto-upload="false"
        :on-change="handleFileChange"
        :on-remove="handleFileRemove"
        accept=".csv"
        :multiple="false"
        :limit="1"
      >
        <el-icon class="el-icon--upload"><UploadFilled /></el-icon>
        <div class="flex flex-col">
          <span class="mb-1 text-sm text-app-black-600">將CSV檔案拖放到此處或</span>
          <span class="text-sm text-app-brand-blue-500">點擊上傳</span>
        </div>
      </el-upload>
    </el-form-item>

    <!-- Tape ID 開關 -->
    <div v-if="showTraceTapeIdSwitch" class="traceTapeId">
      <el-switch v-model="formData.enableTraceTapeId" size="small" class="ml-2" />
      <div class="explain">自動追蹤轉換，提升短網址成效</div>
    </div>

    <!-- CSV 上傳模式：顯示預覽設定 -->
    <template v-if="!useSingleUrlUpload">
      <!-- 預覽設定 -->
      <div class="dividing-line"></div>
      <div class="py-4">
        <div class="flex w-full cursor-pointer items-center justify-between" @click="toggleOgInfo">
          <span class="shorter-url-form-label">自訂預覽縮圖</span>
          <Icon
            icon="ri:arrow-down-s-line"
            width="14px"
            height="14px"
            class="text-app-brand-blue-500 transition-transform duration-300 ease-in-out"
            :class="{ 'rotate-180': showOgInfo }"
          />
        </div>
        <div v-show="showOgInfo" class="w-full pt-4">
          <!-- 圖片上傳 -->
          <div class="ogInfo-upload">
            <ImageUploader
              :cropped-image="croppedImage"
              upload-style="detailed"
              @change="handleUploadImage"
              @error="handleUploadError"
            />
          </div>

          <!-- OG Title -->
          <el-form-item class="form-title" label="">
            <span>標題</span>
            <el-input v-model="formData.title" autocomplete="off" />
          </el-form-item>

          <!-- OG Description -->
          <el-form-item class="form-title" label="">
            <span>描述</span>
            <el-input v-model="formData.description" autocomplete="off" />
          </el-form-item>
        </div>
      </div>
      <div class="dividing-line"></div>
      <!-- 分類標記 -->
      <div class="py-4">
        <span class="shorter-url-form-label">分類標記</span>
        <ul
          v-if="orgSortTagStore.orgSortTags && orgSortTagStore.orgSortTags.length > 0"
          class="mt-3 flex flex-wrap items-center gap-2.5"
        >
          <li v-for="tag in orgSortTagStore.orgSortTags" :key="tag.orgTagId" @click="toggleOrgTag(tag.orgTagId)">
            <OrgSortTag
              :org-tag-id="tag.orgTagId"
              :variant="ORG_SORT_TAG_VARIANTS.TAG"
              :selected="formData.orgTagId === tag.orgTagId"
            />
          </li>
        </ul>
        <div v-else class="flex justify-center py-3 text-app-black-600">尚無分類標記</div>
      </div>

      <!-- 按鈕 -->
      <div class="mt-7 flex items-center justify-between gap-3">
        <BaseButton class="short-url-dialog-btn w-1/2 border-app-black-100 bg-white" @click="handleCancel">
          取消
        </BaseButton>
        <BaseButton
          class="short-url-dialog-btn w-1/2 border-app-brand-blue-500 bg-app-brand-blue-500 text-white hover:bg-app-brand-blue-500/90"
          :loading="isLoading"
          @click="submitForm"
        >
          {{ submitButtonText }}
        </BaseButton>
      </div>
    </template>

    <!-- 個別建立模式：顯示個別短網址表單 -->
    <template v-else>
      <div class="rounded-[10px] border border-solid border-app-black-100 bg-[#fafafa] p-4">
        <SingleShorterUrlForm
          v-model="singleFormData"
          :mode="ShorterDialogMode.SINGLE_CREATE_IN_GROUP"
          :group-data="{
            urlGroupId: formData.urlGroupId ?? '',
            subtagId: formData.subtagId ?? 0
          }"
          :on-submit="handleSingleUrlSubmit"
          :on-cancel="handleCancel"
        />
      </div>
    </template>
  </el-form>

  <!-- 圖片裁切彈窗 -->
  <CropperDialog
    v-model="cropperDialogVisible"
    :selected-image="selectedImage"
    @update:cropped-image="handleUpdateCroppedImage"
    @update:cropped-image-base64="handleUpdateCroppedImageBase64"
  />
</template>

<style lang="scss" scoped>
:deep(.el-form-item__content) {
  color: var(--primary-1);
  font-size: 13px;
  letter-spacing: 1.147px;
}

.form-title {
  width: 100%;
  display: flex;
  flex-direction: column;
  margin-bottom: 25px;

  .radio-group {
    width: 100%;
  }

  .icon {
    margin-left: 4px;
    font-size: 12px;
  }

  .title {
    width: 110px;
    cursor: pointer;
    .icon {
      margin-left: 5px;
    }
  }
}

.upload-switch {
  display: flex;
  justify-content: center;
  align-items: center;
  .switch {
    margin-left: 4px;
  }
  .hintText {
    margin-left: 4px;
  }
}

.dialog-footer {
  display: flex;
  place-content: center;
  margin-top: 40px;
  .btn {
    width: 138px;
    height: 36px;
  }
}

.ogInfo-upload {
  margin-bottom: 16px;
}

.traceTapeId {
  display: flex;
  align-items: center;
  margin-bottom: 16px;
  .explain {
    margin-left: 8px;
    padding-bottom: 2px;
    font-size: 12px;
    color: var(--gray-8c);
  }
}

.dividing-line {
  @apply -mx-6 h-px bg-app-black-50;
}
</style>
